import React, { useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfSub } from './sectionHelpers';

interface Card { title: string; subtitle?: string; image_url?: string; url?: string; }

const ImageCards: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const cards = (c.cards || []) as Card[];
  const columns = Math.min(Math.max(Number(c.columns) || 3, 2), 4);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section className="sb-render sb-render-image-cards" style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 12 }}
          >
            {sfHeadline(c, 'Our Work')}
          </InlineEditText>
          {sfSub(c) && (
            <InlineEditText
              tag="p"
              controlKey="subheadline"
              style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)' }}
            >
              {sfSub(c)}
            </InlineEditText>
          )}
        </div>

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 20,
        }}>
          {cards.map((card, i) => {
            const isHovered = hoveredIndex === i;
            return (
              <a
                key={i}
                href={card.url || '#'}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  display: 'block',
                  position: 'relative',
                  borderRadius: 'var(--brand-card-radius, 12px)',
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                {/* Background image */}
                {card.image_url ? (
                  <img
                    src={card.image_url}
                    alt={card.title || ''}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.4s ease',
                      transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    fontSize: '2rem',
                  }}>
                    🖼
                  </div>
                )}

                {/* Hover overlay — slide up */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  padding: 20,
                }}>
                  <span style={{
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    transform: isHovered ? 'translateY(0)' : 'translateY(12px)',
                    transition: 'transform 0.3s ease',
                    marginBottom: card.subtitle ? 4 : 0,
                  }}>
                    {card.title || `Card ${i + 1}`}
                  </span>
                  {card.subtitle && (
                    <span style={{
                      color: 'rgba(255,255,255,0.75)',
                      fontSize: '0.875rem',
                      transform: isHovered ? 'translateY(0)' : 'translateY(12px)',
                      transition: 'transform 0.35s ease',
                    }}>
                      {card.subtitle}
                    </span>
                  )}
                </div>

                {/* Always-visible light title bar at bottom for accessibility */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '12px 16px 14px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
                  opacity: isHovered ? 0 : 1,
                  transition: 'opacity 0.25s',
                }}>
                  <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                    {card.title || `Card ${i + 1}`}
                  </span>
                </div>
              </a>
            );
          })}

          {cards.length === 0 && (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
              No cards configured yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default ImageCards;
